import { FC, PropsWithChildren, useCallback, useMemo, useState } from "react";

import { Form } from "antd";
import { Tab } from "@hdi-seguros/web-components.tabs";

import { SavedAndSentPreLiquidationsContext } from "./SavedAndSentPreLiquidationsContext";
import { SavedAndSentPreLiquidationTab } from "../../components";
import { PreLiquidationsFilterForm, PreLiquidationTableItem } from "../../interfaces";
import PreLiquidationService from "../../../../../../shared/services/collection/preliquidation";
import { BOOLEAN_VALUES } from "../../../../../../shared/interfaces";

export const SavedAndSentPreLiquidationsProvider: FC<PropsWithChildren> = ({ children }) => {

    const [savedPreLiquidationsFilterForm] = Form.useForm<PreLiquidationsFilterForm>();
    const [sentPreLiquidationsFilterForm] = Form.useForm<PreLiquidationsFilterForm>();

    const [initialDatasourceSavedPreliquidationsTable, setInitialDatasourceSavedPreliquidationsTable] = useState<Array<PreLiquidationTableItem>>([]);
    const [initialDatasourceSentPreliquidationsTable, setInitialDatasourceSentPreliquidationsTable] = useState<Array<PreLiquidationTableItem>>([]);
    
    const [datasourceSavedPreliquidationsTable, setDatasourceSavedPreliquidationsTable] = useState<Array<PreLiquidationTableItem>>([]);
    const [datasourceSentPreliquidationsTable, setDatasourceSentPreliquidationsTable] = useState<Array<PreLiquidationTableItem>>([]);

    const [isLoadingPreLiquidations, setIsLoadingPreLiquidations] = useState<boolean>(false);

    const getSavedPreliquidations = useCallback(async () => {
        try {
            setIsLoadingPreLiquidations(true);

            const response = await PreLiquidationService.getSavedPreliquidations();

            if((response.data)) {

                const datasource: Array<PreLiquidationTableItem> = response.data.preliquidaciones.map(
                    ({ numero, fecha, importeNeto, estado, permiteRetomar }) => ({
                        preliquidationNumber: numero,
                        preliquidationDate: fecha,
                        preliquidationTotalAmount: importeNeto,
                        preliquidationState: estado,
                        shouldResumePreliquidation: permiteRetomar === BOOLEAN_VALUES.TRUE,
                    })
                );

                setInitialDatasourceSavedPreliquidationsTable(datasource);
                setDatasourceSavedPreliquidationsTable(datasource);
            };

        } catch (error) {} finally {
            setIsLoadingPreLiquidations(false);
        }
    }, [ 
        setInitialDatasourceSavedPreliquidationsTable,
        setIsLoadingPreLiquidations,
        setDatasourceSavedPreliquidationsTable,
    ]);

    const getSentPreliquidations = useCallback(async () => {
        try {
            setIsLoadingPreLiquidations(true);

            const response = await PreLiquidationService.getSentPreliquidations();

            if((response.data)) {
                const datasource: Array<PreLiquidationTableItem> = response.data.preliquidaciones.map(
                    ({ numero, fecha, importeNeto, estado }) => ({
                        preliquidationNumber: numero,
                        preliquidationDate: fecha,
                        preliquidationTotalAmount: importeNeto,
                        preliquidationState: estado,
                    })
                );

                setInitialDatasourceSentPreliquidationsTable(datasource);
                setDatasourceSentPreliquidationsTable(datasource);
            };
        } catch (error) {} finally {
            setIsLoadingPreLiquidations(false);
        };
    }, [
        setInitialDatasourceSentPreliquidationsTable,
        setIsLoadingPreLiquidations,
        setDatasourceSentPreliquidationsTable,
    ]);

    const TABS_ITEMS: Array<Tab> = useMemo(
        () => [
            {
                key: '1',
                label: 'Preliquidaciones Guardadas',
                children: (
                    <SavedAndSentPreLiquidationTab 
                        filterForm={savedPreLiquidationsFilterForm} 
                        isSavedPreliquidations
                    />
                ),
                closable: false,
            },
            {
                key: '2',
                label: 'Preliquidaciones Enviadas',
                children: (
                    <SavedAndSentPreLiquidationTab 
                        filterForm={sentPreLiquidationsFilterForm}  
                    />
                ),
                closable: false,
            },
        ], 
        [
            sentPreLiquidationsFilterForm,
            savedPreLiquidationsFilterForm,
        ]
    );

    const values = useMemo(() => ({
        datasourceSavedPreliquidationsTable,
        datasourceSentPreliquidationsTable,
        getSavedPreliquidations,
        getSentPreliquidations,
        initialDatasourceSavedPreliquidationsTable,
        initialDatasourceSentPreliquidationsTable,
        isLoadingPreLiquidations,
        savedPreLiquidationsFilterForm,
        sentPreLiquidationsFilterForm,
        setDatasourceSavedPreliquidationsTable,
        setDatasourceSentPreliquidationsTable,
        TABS_ITEMS,
        setIsLoadingPreLiquidations,
    }), [
        datasourceSavedPreliquidationsTable,
        datasourceSentPreliquidationsTable,
        getSavedPreliquidations,
        getSentPreliquidations,
        initialDatasourceSavedPreliquidationsTable,
        initialDatasourceSentPreliquidationsTable,
        isLoadingPreLiquidations,
        savedPreLiquidationsFilterForm,
        sentPreLiquidationsFilterForm,
        setDatasourceSavedPreliquidationsTable,
        setDatasourceSentPreliquidationsTable,
        TABS_ITEMS,
        setIsLoadingPreLiquidations,
    ]);

    return (
        <SavedAndSentPreLiquidationsContext.Provider value={ values }>
            { children }
        </SavedAndSentPreLiquidationsContext.Provider>
    )
}